import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getBotResponse, chatBotData } from '../../data/chatBotData';
import './ChatBot.css';

const suggestedQuestions = [
  'Who is Rasheed?',
  'Projects?',
  'Contact?',
  'Experience?',
  'Skills?'
];

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: chatBotData.greeting, id: 0 }
  ]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messageIdRef = useRef(1);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isThinking]);

  const sendMessage = (textToSend) => {
    const trimmed = (textToSend || input).trim();
    if (!trimmed) return;

    setInput('');
    setShowSuggestions(false);
    setMessages((prev) => [...prev, { role: 'user', text: trimmed, id: messageIdRef.current++ }]);
    setIsThinking(true);

    const delay = 200 + Math.random() * 200;
    const newId = messageIdRef.current++;
    setTimeout(() => {
      const answer = getBotResponse(trimmed);
      setIsThinking(false);
      setMessages((prev) => [...prev, { role: 'bot', text: answer, id: newId }]);
    }, delay);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <motion.button
        className="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={false}
        animate={{ rotate: isOpen ? 180 : 0 }}
        aria-label={isOpen ? 'Close Q&A' : 'Open Q&A'}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
        </svg>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chatbot-panel"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          >
            <div className="chatbot-header">
              <div className="chatbot-avatar">?</div>
              <div>
                <h3>{chatBotData.botName}</h3>
                <p className="chatbot-status">
                  <span className="status-dot" /> Portfolio questions
                </p>
              </div>
            </div>

            <div className="chatbot-messages">
              {messages.map((msg, i) => (
                <motion.div
                  key={msg.id != null ? msg.id : i}
                  className={`chat-msg ${msg.role}`}
                  initial={{ opacity: 0, x: msg.role === 'user' ? 12 : -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {msg.role === 'bot' && <div className="chat-msg-avatar">?</div>}
                  <div className="chat-msg-bubble">{msg.text}</div>
                </motion.div>
              ))}
              {isThinking && (
                <motion.div className="chat-msg bot" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div className="chat-msg-avatar">?</div>
                  <div className="chat-msg-bubble thinking">
                    <span className="think-dot" /><span className="think-dot" /><span className="think-dot" />
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {showSuggestions && messages.length <= 1 && (
              <div className="chatbot-suggestions">
                {suggestedQuestions.map((q, i) => (
                  <button key={i} type="button" className="suggestion-chip" onClick={() => sendMessage(q)}>
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div className="chatbot-input-wrap">
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask something..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="chatbot-input"
              />
              <button type="button" className="chatbot-send" onClick={() => sendMessage()} aria-label="Send">
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
